import {
  LISTING_BROWSE_PAGE_SIZE as PAGE_SIZE,
  type UseListingBrowseProductsArgs,
} from "./listing-browse-query";
import {
  getListingBrowseLoadingState,
  resolveListingBrowseResult,
} from "./listing-browse-results";
import type {
  usePrimaryListingBrowseData,
  useSmartFallbackData,
} from "./use-listing-browse-data";

export function resolveListingBrowseViewState(
  args: UseListingBrowseProductsArgs,
  page: number,
  primary: ReturnType<typeof usePrimaryListingBrowseData>,
  smartFallback: ReturnType<typeof useSmartFallbackData>,
) {
  const result = resolveListingBrowseResult({
        filters: args,
        hasLocation: primary.queryArgs.hasLocation,
        hasSearch: primary.queryArgs.hasSearch,
        page,
        pageSize: PAGE_SIZE,
        searchResults: primary.searchResults,
        searchTruncated: primary.searchRows.some((row) => row.truncated === true),
        searchIsSettled: primary.searchStatus === "Exhausted",
        nearbyResult: primary.nearbyResult,
        fallbackResult: primary.fallbackResult,
        shouldUseSmartFallback: smartFallback.shouldUseSmartFallback,
        smartFallbackResult: smartFallback.smartFallbackResult,
  });
  const isLoading = !("fromServerRender" in result) && getListingBrowseLoadingState({
    hasSearch: primary.queryArgs.hasSearch,
    searchStatus: primary.searchStatus,
    searchResultCount: primary.searchResults.length,
    requiredSearchResults: primary.requiredSearchResults,
    hasLocation: primary.queryArgs.hasLocation,
    nearbyQueryActive: primary.queryArgs.nearbyArgs !== "skip",
    nearbyResultLoaded: primary.nearbyResult !== undefined,
    fallbackQueryActive: primary.queryArgs.fallbackArgs !== "skip",
    fallbackResultLoaded: primary.fallbackResult !== undefined,
    smartFallbackLoading:
      smartFallback.shouldUseSmartFallback &&
      smartFallback.smartFallbackResult === undefined,
    stateLookupPending: primary.queryArgs.stateLookupPending,
  });
  const hasNextPage = primary.queryArgs.hasSearch
    ? primary.searchResults.length > page * PAGE_SIZE ||
      primary.searchStatus !== "Exhausted"
    : result.hasNextPage;

  return {
    isExpandedSearch: false,
    truncated: false,
    ...result,
    listingTotal: listingTotalFor(result, primary),
    isLoading,
    hasNextPage,
  };
}

function listingTotalFor(
  result: ReturnType<typeof resolveListingBrowseResult>,
  primary: ReturnType<typeof usePrimaryListingBrowseData>,
): number | null {
  if (result.totalCount !== null) return result.totalCount;
  return primary.queryArgs.fallbackArgs !== "skip" ? primary.scopeTotal : null;
}
